/**
 * Board connection watch — pads that moved, never invent seat names.
 * Soft residual: safe contract path when op id absent from generated client.
 */
import { useState } from "react";
import { html, _F, Ico, KV, Card, Field, Input, Alert } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { dismissConnection, learnSeat, pollLearnResult } from "./board-api.js";

const STATE_TONE = {
  connected: "go",
  disconnected: "warn",
  noisy: "warn",
  unreadable: "bad",
  changed: "",
};

/** Missing contract ops must not crash the desk (manifest lag / flag off at gen time). */
export function safeBoardConnectionsPath(deviceId) {
  const did = String(deviceId || "").trim();
  if (!did) return null;
  try {
    return path("get__devices_p_connections", { pathParams: [did] });
  } catch (_e) {
    return "/devices/" + encodeURIComponent(did) + "/connections";
  }
}

export function ConnectionWatch({ deviceId, boardLive, toast, onBoardReload }) {
  const did = String(deviceId || "").trim();
  // Fail-closed: path() throw must not abort React render.
  const conn = useApi(boardLive && did ? safeBoardConnectionsPath(did) : null);
  const { busy, error } = useAction();
  const [reason, setReason] = useState("Installer acknowledged · not wiring this pad");
  const [learnSeatId, setLearnSeatId] = useState("HDR-15");
  const [busyLocal, setBusyLocal] = useState(false);

  if (!boardLive) {
    return html`<${Alert} tone="warn" title="Connection watch needs the live board surface"
      say="Turn on device board for this deployment first. Hotplug lists stay empty until the server arms the board — nothing is invented here."/>`;
  }

  const items = (conn.data && (conn.data.connections || conn.data.items)) || [];
  const missed = conn.data && conn.data.events_missed != null ? Number(conn.data.events_missed) : 0;
  const actionable = items.filter((c) => c.actionable);

  const dismiss = async (gpio) => {
    setBusyLocal(true);
    const out = await dismissConnection(did, gpio, { reason, dismissed: true });
    setBusyLocal(false);
    if (!out.ok) { toast(out.error, "bad"); return; }
    toast("Pad dismissed · gpio " + gpio, "warn");
    conn.reload();
  };

  const startLearn = async (row) => {
    const seat = String(learnSeatId || row.seat_id || "").trim().toUpperCase();
    if (!/^HDR-\d+$/i.test(seat)) {
      toast("Pick an HDR-N seat for learn — gpio alone cannot name the silk", "warn");
      return;
    }
    setBusyLocal(true);
    const started = await learnSeat(did, seat, { window_s: 20 });
    if (!started.ok) { setBusyLocal(false); toast(started.error, "bad"); return; }
    toast("Learning open · short header seat for gpio " + row.gpio + " to GND", "warn");
    const polled = await pollLearnResult(did);
    setBusyLocal(false);
    if (!polled.ok) { toast(polled.error, "bad"); return; }
    const st = polled.learn && polled.learn.state;
    toast(
      "Learn " + st + (polled.learn.gpio != null ? (" · gpio " + polled.learn.gpio) : ""),
      st === "resolved" ? "go" : "warn",
    );
    conn.reload();
    if (onBoardReload) onBoardReload();
  };

  return html`<div className="conn-watch conn-watch--liquid" data-cmc-page="page148-conn-watch"
    data-soft-api-path-paint-forbidden="1">
    <${Card} title="Connection watch" meta="hotplug · dismiss · learn"
        sub="Pads that moved on this device. gpio stays unnamed until learn binds a seat. Lists come only from the server.">
      <div className="btn-row" style=${{ marginBottom: 10 }}>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${busy || busyLocal || conn.loading}
          onClick=${() => { conn.reload && conn.reload(); toast && toast("Connection watch refreshed", "go"); }}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
      </div>
      ${missed > 0 && html`<${Alert} tone="warn" title="Events missed in transit"
        say=${missed + " connection events lost — this list is incomplete, not filled in the browser."}/>`}
      ${conn.loading && !conn.data ? html`<p className="note">Loading watches…</p>` : null}
      ${conn.error && !conn.data ? html`<p className="note">${String(conn.error)}</p>` : null}
      <${KV} k="Watched pads" v=${String((conn.data && conn.data.watched) || items.length || 0)}/>
      <${KV} k="Actionable" v=${String(actionable.length)} tone=${actionable.length ? "go" : ""}/>
      <${KV} k="Epoch" v=${String((conn.data && conn.data.epoch) || "—")}/>

      <${Field} label="Dismiss reason" hint="Required for dismiss · audited">
        <${Input} icon="book" value=${reason} onChange=${(e) => setReason(e.target.value)}/>
      <//>
      <${Field} label="Learn seat" hint="HDR-N — server never invents this from gpio alone">
        <${Input} icon="chip" value=${learnSeatId} onChange=${(e) => setLearnSeatId(e.target.value)}/>
      <//>

      <div className="rows" style=${{ marginTop: 12 }}>
        ${!items.length ? html`<p className="note">No pad changes yet. Plug a contact or run bias/learn on the unit.</p>` : null}
        ${items.slice(0, 32).map((c) => html`
          <div key=${"g" + c.gpio} className="row row--say" data-state=${c.actionable ? "on" : "off"}>
            <${Ico} d="chip" w=${14}/>
            <span className="main">
              <b>gpio ${c.gpio}${c.seat_id ? " · " + c.seat_id : " · unnamed"}</b>
              <span>${c.state || "—"} · ${c.bias || c.to_bias || "—"}
                ${c.needs_learn ? " · needs learn" : ""}
                ${c.seat_bound ? " · bound" : ""}
                ${c.actionable ? " · actionable" : ""}</span>
            </span>
            <span className="chip tiny" data-tone=${STATE_TONE[c.state] || ""}>${c.state || "—"}</span>
            ${c.actionable && html`
              <button className="btn btn--sm btn--ai" disabled=${busy || busyLocal}
                onClick=${() => startLearn(c)}>Learn seat</button>`}
            <button className="btn btn--sm btn--ghost" disabled=${busy || busyLocal}
              onClick=${() => dismiss(c.gpio)}>Dismiss</button>
          </div>`)}
      </div>
      ${error && html`<${Alert} tone="bad" title="Watch error" say=${error}/>`}
    <//>
  </div>`;
}
